import { createRef } from 'react';
import { reviewActionOr, reviewChangesOr, updateElementAudit } from './audit';
import { collectionChanges } from './change-api';
import {
  autoCompleteRequest,
  CodesetResult,
  findDxOrProcedureRequest,
} from './codesets';
import { getAutoCompleteInput, inputValueSelection } from './controls';
import { utcDateString } from './date-time';
import { Audit, CodeSet, LostRevenue, Provider } from './domain';
import { formApi, FormApi, providerField } from './form';
import { listApi, ListApi } from './list';
import {
  deleteLostRevenuesRequest,
  saveLostRevenueRequest,
  updateLostRevenueRequest,
} from './lost-revenue';
import { VisitCode, WorksheetContext } from './visit-context';
import { isReviewWorkflowState } from './workflow-api';
import { WorksheetFormApi } from './worksheet-form-api';

export interface LostRevenueApi extends WorksheetFormApi<LostRevenue> {
  list?: ListApi<LostRevenue>;
}

let lastCodeSet: CodeSet = null;
export function lostRevenueApi(context: WorksheetContext): LostRevenueApi {
  const { worksheet, status, config, lostRevenue, readonly, getServiceDate } =
    context;
  const { worksheetId, owningProvider } = worksheet;
  const { providers } = config;
  const lostRevenueReasons = config.lostRevenueList;

  const defaultFilter = isReviewWorkflowState() ? 'all' : 'active';

  const id = (a:LostRevenue) => (a.lostRevenueId || '') + a.cptCode;
  const list = listApi({
    id,
    defaultFilter,
    all: lostRevenue,
    equals: (a, b) => id(a) === id(b),
    active: (v) => v.active,
    setActive: (v) => (v.active = true),
    setInactive: (v) => (v.active = false),
  });

  let editing: LostRevenue;
  function editItem(v: LostRevenue) {
    api.onEdit();
    list.actions.edit(v);
    editing = v;

    form.setValues({
      cptCode: { code: v.cptCode } as any,
      cptDescription: v.cptDescription,
      reason: reason.setListValue((c: VisitCode) => c.description === v.reason),
      provider: provider.setListValue(v.provider),
      comment: v.comment,
    });

    focusInput(v?.cptCode);
  }

  function addItem(v: LostRevenue) {
    async function request(update?: (v: any) => void) {
      if (update) {
        update(v);
      }

      v.lostRevenueId = editing?.lostRevenueId;

      const response = await (editing
        ? updateLostRevenueRequest(v)
        : saveLostRevenueRequest(v));

      if (response.success) {
        const item = response.result;
        if (update) {
          update(item);
        }

        lastFn = () => removeItem(item);

        if (editing) {
          list.actions.replace(item, (v) => v.cptCode === editing.cptCode);
        } else {
          list.actions.add(item);
        }

        resetForm();
      } else {
        setStatus((response.errors || []).join(' '));
      }

      editing = null;
    }

    const edit = list.data.getState().editing;
    if (edit) {
      reviewChangesOr(
        {
          title: 'Review Changes For Lost Revenue ',
          original: edit,
          update: v,
          changeReasons: {
            actualCPTCode: config.auditDownCode,
            targetCPTCode: config.auditDownCode,
          },
          fieldNames: ['reason', 'provider', 'cptCode'],
          onComplete: (a: Audit) => {
            request((v) => updateElementAudit(v, a));
          },
          onCancel: () => (editing = null),
        },
        request
      );
    } else {
      reviewActionOr(
        {
          title: 'Add Lost Revenue ',
          list: context.config.auditLostRevenue,
          onComplete: (a) => {
            a.action = 'CREATE';
            request((v) => updateElementAudit(v, a));
          },
          onCancel: () => (editing = null),
        },
        request
      );
    }
  }

  let lastFn: () => void;

  function removeItem(v: LostRevenue) {
    lastFn = () => addItem(v);

    async function request(): Promise<boolean> {
      const response = await deleteLostRevenuesRequest({
        worksheetId: worksheet.worksheetId,
        lostRevenueId: v.lostRevenueId,
      });
      if (response.success) {
        list.actions.remove(v);
        return Promise.resolve(true);
      } else {
        setStatus((response.errors || []).join(' '));
      }
      return Promise.resolve(false);
    }

    reviewActionOr(
      {
        title: 'Remove Lost Revenue ',
        list: context.config.auditLostRevenue,
        onComplete: (a) => {
          a.action = 'DELETE';
          v.audit = a;
          request();
        },
      },
      request
    );
  }

  const initialValues = {
    cptCode: null,
    reason: null,
    provider: owningProvider,
    comment: '',
  };

  const setStatus = status.getState().setStatus;

  let lastCode = '';
  const form: FormApi<Partial<LostRevenue>> = formApi({
    disabled: readonly,
    values: initialValues,
    resetValues: null,
    fields: {
      cptCode: {
        label: 'CPT',
        required: true,
        list: [],
        inputRef: createRef(),
        disableClearable: false,
        listLabel: (c: CodeSet) =>
          (c &&
            c.description &&
            c.code + ' - ' + c.description.longDescription) ||
          '',
        textLabel: (c: CodeSet) => (c && c.code) || '',
        onEnter: (v) => {
          findCode(v?.code || lastCode);
        },
        onEnterValid: () => {
          form.submit();
        },
        onChange: (c: CodeSet) => {
          if (c?.code) {
            lastCode = c.code;
          }
        },
        onInputChange: async (v: string) => {
          v = (v || '').trim();

          if (v?.length > 1) {
            lastCode = v;
            findCode(v);
          } else {
            lastCode = lastCodeSet = null;
          }
          console.log('input change', v);
        },
      },
      reason: {
        label: 'Reason',
        list: lostRevenueReasons,
        listLabel: (v) => v.description,
        required: true,
        onEnterValid: () => {
          console.log('enter valid reason');
          form.submit();
        },
      },
      provider: providerField(providers, {
        onEnterValid: () => form.submit(),
      }),
      comment: {
        label: 'Comments',
        onEnter: () => form.submit(),
      },
    },
    valid: (v) => {
      const { cptCode, reason, provider } = v;
      const c: any = (
        cptCode?.code ||
        lastCode ||
        lastCodeSet?.code ||
        ''
      ).trim();
      if (!c?.length) {
        return false;
      }
      if (!reason?.description) {
        return false;
      }
      if (!provider) {
        return false;
      }
      lastCode = c;
      return true;
    },
    onSubmit: async (values) => {
      const c: any = values.cptCode || lastCode || lastCodeSet?.code || null;
      const v = typeof c === 'string' ? c : c ? c.code : '';
      // console.log('onSubmit code', v, values)
      if (!v?.length) {
        return;
      }

      setStatus(null);
      let serviceDate = getSearchDate();

      const codeResult: CodesetResult = await findDxOrProcedureRequest({
        serviceDate,
        code: v,
      });

      const { success, errors } = codeResult;
      let cptCode = v;
      if (success) {
        const code = codeResult.result;
        if (code.codeType === 'ICD_10') {
          setStatus('Tracking code must be a procedure.');
          return;
        } else {
          cptCode = code.code;

          addItem({
            worksheetId,
            cptCode,
            reason: values?.reason?.description,
            provider: values?.provider,
            comment: values?.comment,
          });
        }
      } else {
        setStatus(errors ? errors.join(' ') : 'Error');
        return;
      }
    },
  });

  function resetForm() {
    form.setValues(initialValues, true);

    const { cptCode } = form.controls;
    lastCode = lastCodeSet = null;

    cptCode.clear({ code: '' });
    cptCode.clear();

    focusInput('');
  }

  function getSearchDate() {
    const date: Date = getServiceDate();
    return utcDateString(date);
  }

  async function findCode(v: string) {
    if (v?.length >= 2) {
      lastCode = v;

      let serviceDate = getSearchDate();
      let r: CodeSet[] = await autoCompleteRequest({ code: v, serviceDate });
      if (r) {
        r = r.filter((v: CodeSet) => v.codeType !== 'ICD_10');
        if (r.length) {
          // lastCodeSet = r[0];
          form.controls.cptCode.list.getState().setValue(r);

          return Promise.resolve(true);
        }
      }
    }
    return Promise.resolve(false);
  }

  const { reason, provider, cptCode } = form.controls;

  const inputElement = getAutoCompleteInput(cptCode.inputRef);
  const focusInput = inputValueSelection(inputElement);

  const listChanges = collectionChanges(list, {
    changeLabel: (v: LostRevenue[]) => v.map((u) => u.cptCode).join(', '),
  });

  function setPhysician(providerId: String) {
    const defaultProvider = providers.filter(
      (v: Provider) => v.providerId === providerId
    )[0];

    if (defaultProvider) {
      initialValues.provider = defaultProvider;
    }
    provider.setListValue((v: Provider) => v.providerId === providerId);
  }

  const api = {
    form,
    list,
    listChanges,
    context,
    setPhysician,
    editItem,
    addItem,
    removeItem,
    onEdit: () => null,
  };
  return api;
}
